import React, { useState, useEffect, useRef } from "react";
import Container from "../../../components/Container/Container";
import Table from "../../../components/Table/Table";
import { API, ROUTER } from "../../../constants/env";
import { formatFirstName, formatLastName, formatProfilName } from "../../../utils/displayName";
import swal from "sweetalert";
import { Link } from "react-router-dom";

const TRI_LABEL = { premier: "T1", deuxieme: "T2", troisieme: "T3" };
const TRI_FULL = {
  premier: "1er trimestre",
  deuxieme: "2ème trimestre",
  troisieme: "3ème trimestre",
};
const NIVEAUX = [
  { value: "1er", label: "1ère Année" },
  { value: "2eme", label: "2ème Année" },
  { value: "3eme", label: "3ème Année" },
  { value: "4eme", label: "4ème Année" },
  { value: "5eme", label: "5ème Année" },
  { value: "6eme", label: "6ème Année" },
];

const ListCarnet = () => {
  const fields = [
    { title: "Prénom", field: "firstName" },
    { title: "Nom", field: "lastName" },
    { title: "Classe", field: "classe" },
    { title: "Triméstre", field: "trimestre" },
  ];
  const [data, setData] = useState([]);
  const [progress, setProgress] = useState(null); // {phase, done, total, name, className}
  const pollRef = useRef(null);

  useEffect(() => {
    localStorage.removeItem("tris");
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Impression groupée : 1 seul PDF fusionné, avec barre de progression (poll).
  const handleClassPrint = async (classeId, termestre, className) => {
    if (!classeId || !termestre) return;
    const triFull = TRI_FULL[termestre] || termestre;
    setProgress({
      phase: "render",
      mode: "class",
      done: 0,
      total: 0,
      name: "",
      className,
      triFull,
    });

    // Poll du statut côté serveur pour faire avancer la barre.
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetch(API.generatePdf.classMergedStatus(classeId, termestre), {
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((r) => r.json())
        .then((p) => {
          if (p && p.phase && p.phase !== "idle") {
            setProgress((prev) => ({
              ...(prev || {}),
              ...p,
              mode: "class",
              className,
              triFull,
            }));
          }
        })
        .catch(() => {});
    }, 2000);

    try {
      const resp = await fetch(API.generatePdf.classMerged(classeId, termestre), {
        headers: { "x-auth-token": localStorage.getItem("token") },
      });
      if (!resp.ok) {
        let msg = `Erreur ${resp.status}`;
        try {
          const j = await resp.json();
          if (j && j.message) msg = j.message;
        } catch (e) {}
        const err = new Error(msg);
        err.status = resp.status;
        throw err;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const triLabel = TRI_LABEL[termestre] || termestre;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${className}-${triLabel}.pdf`.replace(/\s+/g, "-");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (e && e.status === 404) {
        swal("Aucun carnet", e.message || "Aucun carnet pour cette sélection.", "info");
      } else {
        swal("Erreur", e.message || "Erreur lors de la génération du PDF", "error");
      }
    } finally {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setProgress(null);
    }
  };

  // Impression par niveau : 1 ZIP = 1 PDF par classe du niveau.
  const handleLevelPrint = async (annee, termestre, label) => {
    if (!annee || !termestre) return;
    const triFull = TRI_FULL[termestre] || termestre;
    setProgress({
      phase: "render",
      mode: "level",
      className: label,
      triFull,
      classDone: 0,
      classTotal: 0,
      carnetDone: 0,
      carnetTotal: 0,
      carnetName: "",
    });
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetch(API.generatePdf.classLevelStatus(annee, termestre), {
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((r) => r.json())
        .then((p) => {
          if (p && p.phase && p.phase !== "idle") {
            setProgress((prev) => ({
              ...(prev || {}),
              ...p,
              mode: "level",
              className: label,
              triFull,
            }));
          }
        })
        .catch(() => {});
    }, 2000);

    try {
      const resp = await fetch(API.generatePdf.classLevelZip(annee, termestre), {
        headers: { "x-auth-token": localStorage.getItem("token") },
      });
      if (!resp.ok) {
        let msg = `Erreur ${resp.status}`;
        try {
          const j = await resp.json();
          if (j && j.message) msg = j.message;
        } catch (e) {}
        const err = new Error(msg);
        err.status = resp.status;
        throw err;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const triLabel = TRI_LABEL[termestre] || termestre;
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}-${triLabel}.zip`.replace(/\s+/g, "-");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (e && e.status === 404) {
        swal("Aucun carnet", e.message || "Aucun carnet pour ce niveau.", "info");
      } else {
        swal("Erreur", e.message || "Erreur lors de la génération.", "error");
      }
    } finally {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setProgress(null);
    }
  };

  const fetchData = (
    currentPage,
    count,
    search,
    setTotalPage,
    setTotal,
    idClasse,
    termestre,
    level,
    sortBy,
    sortDir
  ) => {
    fetch(
      API.carnetStudent.list +
      `?page=${currentPage}&count=${count}&searchQuery=${search}&filterByClasse=${idClasse}&sortBy=${sortBy || ""}&sortDir=${sortDir || ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": localStorage.getItem("token"),
        },
      }
    )
      .then((res) => res.json())
      .then((response) => {
        if (response.error) {
          return swal("Error", response.message, "error");
        }

        const result = response.data.map((elem) => ({
          _id: elem._id,
          firstName: formatFirstName(elem.student.profil.firstName),
          lastName: formatLastName(elem.student.profil.lastName),
          classe: elem.student.classe.name,
          trimestre: (
            <div>
              {" "}
              {elem.reportCards.map((card) => (
                <Link
                  as="button"
                  className="btn btn-light-primary btn-sm font-weight-bold mx-1"
                  to={`${ROUTER.CARNETS.EDIT(card._id)}/${formatProfilName(
                    elem.student.profil
                  )}`}
                  onClick={() => localStorage.setItem("tris", card.semestre)}
                >
                  {card.semestre === "premier"
                    ? "1er trim."
                    : card.semestre === "deuxieme"
                      ? "2ème trim."
                      : "3ème trim."}
                </Link>
              ))}
            </div>
          ),
        }));
        setData(result);
        setTotalPage(response.totalPage);
        setTotal(response.total);
      })
      .catch((e) => {
        swal("Error", e.message, "error");
      });
  };

  // Barre de progression (modal)
  let pct = 0;
  if (progress) {
    if (progress.mode === "level") {
      const ct = progress.classTotal || 0;
      const cf = progress.carnetTotal
        ? (progress.carnetDone || 0) / progress.carnetTotal
        : 0;
      pct = ct ? Math.min(100, Math.round((((progress.classDone || 0) + cf) / ct) * 100)) : 0;
    } else if (progress.total > 0) {
      pct = Math.min(100, Math.round((progress.done / progress.total) * 100));
    }
  }

  return (
    <Container name="Carnets de Notes">
      <Table
        name="Carnets"
        fields={fields}
        data={data}
        classeApi={API.carnetStudent.listStudents}
        Termestre={true}
        onClassPrint={handleClassPrint}
        niveauOptions={NIVEAUX}
        onLevelPrint={handleLevelPrint}
        printingClass={!!progress}
        pageName="carnets"
        fetchData={fetchData}
        sortableFields={["firstName", "lastName"]}
        defaultSort={{ by: "lastName", dir: "asc" }}
        showSearch={true}
        hideActions={true}
        stopCheckbox={true}
      />

      {progress && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "28px 32px",
              width: 420,
              maxWidth: "90vw",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
              textAlign: "center",
            }}
          >
            <h4 style={{ margin: "0 0 6px", fontWeight: 600 }}>
              Génération des carnets…
            </h4>
            <p style={{ margin: "0 0 18px", color: "#7a7a7a", fontSize: 14 }}>
              <strong>{progress.className || "Classe"}</strong>
              {progress.triFull ? ` — ${progress.triFull}` : ""}
            </p>

            <div
              style={{
                height: 12,
                background: "#eef0f3",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: "#185fa5",
                  transition: "width .3s ease",
                }}
              />
            </div>

            <div style={{ marginTop: 14, fontSize: 14, color: "#444" }}>
              {progress.mode === "level" ? (
                progress.phase === "merge" ? (
                  <span>Assemblage du ZIP…</span>
                ) : (
                  <div>
                    <div>
                      Classe{" "}
                      <strong>
                        {Math.min(
                          (progress.classDone || 0) + 1,
                          progress.classTotal || 0
                        )}
                      </strong>{" "}
                      / {progress.classTotal || 0}
                      {progress.className ? (
                        <span style={{ color: "#888" }}> — {progress.className}</span>
                      ) : null}
                    </div>
                    {progress.carnetTotal > 0 ? (
                      <div style={{ marginTop: 6, fontSize: 13, color: "#777" }}>
                        Carnet {progress.carnetDone} / {progress.carnetTotal}
                        {progress.carnetName ? ` — ${progress.carnetName}` : ""}
                      </div>
                    ) : null}
                  </div>
                )
              ) : progress.phase === "merge" ? (
                <span>Assemblage du PDF final…</span>
              ) : progress.total > 0 ? (
                <span>
                  Carnet <strong>{progress.done}</strong> / {progress.total}
                  {progress.name ? (
                    <span style={{ color: "#888" }}> — {progress.name}</span>
                  ) : null}
                </span>
              ) : (
                <span>Préparation…</span>
              )}
            </div>
            <p style={{ margin: "12px 0 0", fontSize: 12, color: "#aaa" }}>
              La première génération peut prendre jusqu'à 1 minute.
            </p>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ListCarnet;
